/**
 * Feature Flags
 *
 * Centralized feature flag configuration for the application.
 * Feature flags are controlled via environment variables.
 */

/**
 * Check if a feature flag is enabled.
 *
 * @param flag - Environment variable name (e.g., 'VITE_FEATURE_CONVERSATIONAL_AGENT')
 * @param defaultValue - Value to use when `flag` is unset in the environment (default: false)
 * @returns true if the feature is enabled, false otherwise
 */
function isFeatureEnabled(flag: string, defaultValue = false): boolean {
  const value = import.meta.env[flag];

  // Handle various truthy/falsy values
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    if (value.toLowerCase() === 'true' || value === '1') return true;
    if (value.toLowerCase() === 'false' || value === '0') return false;
  }

  // Not explicitly set — fall back to this flag's own default
  return defaultValue;
}

/**
 * Feature flags available in the application
 */
export const featureFlags = {
  /**
   * Conversational AI Agent for PRD Assistance
   *
   * Enables the "Ask AI" button in PRD inputs that opens a modal
   * for conversational assistance with PRD content.
   *
   * @default false
   */
  conversationalAgent: isFeatureEnabled('VITE_FEATURE_CONVERSATIONAL_AGENT'),

  /**
   * Global Shell — persistent left Sidebar + slim TopBar
   *
   * Replaces the top tab bar (Header) with the new navigation shell
   * (Sidebar, TopBar, MobileNavDrawer). On by default — it's now the primary
   * experience. Set VITE_FEATURE_GLOBAL_SHELL=false locally to opt back into
   * the legacy Header (e.g. for comparison, or while debugging a shell-only
   * issue). The primary local E2E server and CI's E2E web server both pin
   * this to false explicitly (apps/web/.env.e2e, .github/workflows/ci.yml)
   * so their existing legacy-Header specs keep passing regardless of this
   * default — see apps/web/e2e/README.md.
   *
   * @default true
   */
  globalShell: isFeatureEnabled('VITE_FEATURE_GLOBAL_SHELL', true),
} as const;
